import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError, tokenStore } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { PROJECT_STAGES, PROJECT_STAGES_BY_KEY, SUB_STATUSES_BY_KEY, BLOCKING_SUB_STATUSES } from "@/lib/workflow";
import {
  SheetConsumptionCard, ProductionHoursCard, PurchaseOrdersCard,
  TransportExpensesCard, InstallationExpensesCard, ProjectMarginCard,
} from "./project/costing";
import {
  ChevronLeft, Loader2, Download, FileSpreadsheet, AlertTriangle,
  Phone, Mail, MapPin, Clock, CheckCircle2, XCircle, Send, RotateCw, User,
  Pencil, Trash2, CalendarClock, Wallet, Activity, FileText, PenTool,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_LABELS = Object.fromEntries(Object.entries(PROJECT_STAGES_BY_KEY).map(([k, v]) => [k, v.label]));
// Backend PROJECT_STAGES defines the owning role for each stage.
const STAGE_OWNER_ROLE = {
  payment_verification: "accounts",
  design_drawings: "production_design",
  customer_approval: "sales_coordination",
  production_coordination: "production_coordination",
  production: "production",
  pre_installation: "production",
  dispatch: "sales",
  installation: "installation",
  review_closeout: "sales",
  completed: null,
};
const ROLE_LABEL = {
  super_admin: "Super Admin", sales: "Sales", sales_coordination: "Sales Coord",
  accounts: "Accounts", design: "Design", production_design: "Prod Design",
  production_coordination: "Prod Coord",
  admin: "Admin", production: "Production", installation: "Installation", dealer: "Dealer",
};

// The visible project label. Backend project_id is "HK - <customer> - <project>";
// per user request we drop the "HK - " prefix in the UI without changing storage
// (keeps historical URLs, backups, and filenames stable).
const displayPid = (pid) => (pid || "").replace(/^HK\s*-\s*/i, "");
const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/project/${id}`); setP(data); }
    catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg || "Updated"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (error) return <DesktopLayout><div className="text-red-600">{error}</div></DesktopLayout>;
  if (!p) return <DesktopLayout><div className="text-slate-500">Loading…</div></DesktopLayout>;
  const isBusiness = ["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "dealer"].includes(user?.role);
  // Buyout Items export (a.k.a. PR Excel) restricted to procurement operators.
  const canBuyout = ["super_admin", "admin", "production_coordination"].includes(user?.role);
  const pdfUrl = `${api.defaults.baseURL}/project/${p.id}/print.pdf`;
  const xlsxUrl = `${api.defaults.baseURL}/project/${p.id}/export.xlsx`;
  // Production & Installation get a phone-width layout (Item — 2026-02)
  const mobileFirst = ["production", "installation"].includes(user?.role);
  const wrapClass = mobileFirst ? "max-w-[520px] mx-auto overflow-x-hidden" : "";

  return (
    <DesktopLayout>
      <div className={wrapClass} data-testid={mobileFirst ? "project-mobile-view" : undefined}>
      <Link to="/projects" className="hk-tiny-label text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> All projects
      </Link>

      {p.active_issue_id && (
        <ActiveIssueBanner p={p} onOpen={() => {
          // Scroll to Issues card so the user can jump straight to context
          const el = document.querySelector('[data-testid="issues-card"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }} />
      )}

      <div className="flex flex-wrap items-start justify-between gap-4 mt-2 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-black uppercase px-2 py-1 rounded-sm bg-slate-900 text-white">
              {STAGE_LABELS[p.stage] || p.stage}
            </span>
            {p.sub_status && p.sub_status !== "in_progress" && SUB_STATUSES_BY_KEY[p.sub_status] && (
              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-1 rounded-sm"
                style={{ color: SUB_STATUSES_BY_KEY[p.sub_status].color, backgroundColor: SUB_STATUSES_BY_KEY[p.sub_status].bg }}
                data-testid="project-sub-status">
                {BLOCKING_SUB_STATUSES.has(p.sub_status) && <AlertTriangle className="w-3 h-3" />}
                {SUB_STATUSES_BY_KEY[p.sub_status].label}
              </span>
            )}
            {p.status !== "active" && (
              <span className="text-[10px] font-black uppercase px-2 py-1 rounded-sm bg-amber-100 text-amber-900 border border-amber-200">
                {p.status} — {p.blocking_reason}
              </span>
            )}
          </div>
          {p.sub_status_reason && BLOCKING_SUB_STATUSES.has(p.sub_status) && (
            <div className="text-xs bg-red-50 border border-red-200 rounded-sm p-2 mb-1 mt-1 inline-block" data-testid="project-blocked-reason">
              <b>{SUB_STATUSES_BY_KEY[p.sub_status]?.label}:</b> {p.sub_status_reason}
            </div>
          )}
          <div className="font-mono text-sm sm:text-base font-bold text-slate-700 tracking-tight" data-testid="project-id-display">{displayPid(p.project_id)}</div>
          <h1 className="text-3xl font-black tracking-tighter mt-1" style={{ fontFamily: "Chivo" }}>{p.name}</h1>
          {/* Cross-links back to originating quote + enquiry, so Sales Coord
              can jump to the enquiry and request extra 2D/3D drawings for
              the same project. Hidden from Production and Installation roles
              so shop-floor staff aren't distracted with commercial docs. */}
          {["super_admin", "admin", "sales", "sales_coordination", "accounts", "production_coordination"].includes(user?.role) && (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
              {p.quote_id && (
                <Link to={`/quotes/${p.quote_id}`}
                  className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900 font-bold"
                  data-testid="project-quote-link">
                  <FileText className="w-3 h-3" /> View quote
                </Link>
              )}
              {p.enquiry_id && (
                <Link to={`/enquiries/${p.enquiry_id}`}
                  className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900 font-bold"
                  data-testid="project-enquiry-link">
                  <Send className="w-3 h-3" /> Open enquiry
                </Link>
              )}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
            <span>{p.customer_name}</span>
            {isBusiness && p.customer_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {p.customer_phone}</span>}
            {isBusiness && p.customer_email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {p.customer_email}</span>}
            {p.estimated_delivery_date && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Delivery: {p.estimated_delivery_date}</span>}
            {p.dealer_name && <span>Dealer: <b>{p.dealer_name}</b></span>}
            {p.assigned_sales_name && (
              <span className="inline-flex items-center gap-1" data-testid="project-sales-person">
                <User className="w-3.5 h-3.5" /> Sales: <b>{p.assigned_sales_name}</b>
              </span>
            )}
            {(p.stage_owner_role || STAGE_OWNER_ROLE[p.stage]) && (
              <span className="inline-flex items-center gap-1" data-testid="stage-owner">
                Stage owner: <b>{ROLE_LABEL[p.stage_owner_role || STAGE_OWNER_ROLE[p.stage]] || (p.stage_owner_role || STAGE_OWNER_ROLE[p.stage])}</b>
                {(p.stage_owner_role || STAGE_OWNER_ROLE[p.stage]) === "sales" && p.assigned_sales_name ? ` (${p.assigned_sales_name})` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["super_admin", "sales", "sales_coordination", "production_coordination"].includes(user?.role) && (
            <button type="button" data-testid="project-edit-btn"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-xs">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          {user?.role === "super_admin" && (
            <button type="button" data-testid="project-delete-btn"
              onClick={async () => {
                const label = (p.project_id || "").replace(/^HK\s*-\s*/i, "");
                if (!window.confirm(`Delete project "${label}"? This cannot be undone.`)) return;
                setBusy(true);
                try {
                  await api.delete(`/project/${p.id}`);
                  toast.success("Project deleted");
                  navigate("/projects");
                } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
              }}
              className="inline-flex items-center gap-1.5 border border-red-300 hover:bg-red-50 text-red-700 font-bold px-3 py-2 rounded-sm text-xs">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-xs" data-testid="project-print">
            <Download className="w-3.5 h-3.5" /> Print pack
          </a>
          <a href={xlsxUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-xs" data-testid="project-xlsx">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </a>
          {canBuyout && (
            <a href={`${api.defaults.baseURL}/project/${p.id}/pr.xlsx`} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-xs" data-testid="project-buyout">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Buyout Items
            </a>
          )}
          {["super_admin", "production_design"].includes(user?.role) && (
            <a href={`${api.defaults.baseURL}/project/${p.id}/item-ids.csv`} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-xs" data-testid="project-item-ids">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Item IDs (CSV)
            </a>
          )}
        </div>
      </div>

      <ProjectSummaryBanner p={p} user={user} />

      {p.has_newer_quote_version && (
        <div className="hk-card mb-6 p-4 border-l-4 border-amber-500 bg-amber-50 flex items-start gap-3" data-testid="newer-quote-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <div className="font-black text-amber-900">
              A newer quote version exists for this project
            </div>
            <div className="text-amber-800 mt-0.5">
              This project was built from <b>v{p.converted_quote_version}</b>.
              The latest version is <b>v{p.latest_quote_version}</b>
              {p.latest_quote_number ? <> · <span className="font-mono">{p.latest_quote_number}</span></> : null}.
              Modules, prices and totals here are frozen; review the newer quote before dispatching.
            </div>
          </div>
          {p.latest_quote_id && (
            <Link to={`/quotes/${p.latest_quote_id}`}
                  className="shrink-0 inline-flex items-center gap-1 text-[10px] font-black uppercase px-3 py-2 rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="newer-quote-open">
              Open latest quote
            </Link>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <QuoteSnapshotCard snapshot={p.quote_snapshot} isBusiness={isBusiness} pc={p.production_coordination} compact={mobileFirst} />
          <TimelineCard timeline={p.timeline} />
          <IssuesCard projectId={p.id} projectPid={p.project_id} />
        </div>
        <div className="space-y-4 min-w-0">
          <StageActions p={p} act={act} busy={busy} user={user} />
          <RevertStageButton p={p} act={act} user={user} />
          <RequestExtraDrawingsCard p={p} user={user} />
          <SpecialRequirementsCard p={p} act={act} user={user} />
          <FilesCard p={p} act={act} user={user} />
          <PurchaseRequestsCard p={p} user={user} />
          <SheetConsumptionCard p={p} user={user} />
          <ProductionHoursCard p={p} user={user} />
          <PurchaseOrdersCard p={p} user={user} />
          <TransportExpensesCard p={p} user={user} />
          <InstallationExpensesCard p={p} user={user} />
          <ProjectMarginCard p={p} user={user} />
          <ProcurementCard p={p} act={act} user={user} />
        </div>
      </div>
      {editing && (
        <EditProjectModal p={p} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await load(); }} />
      )}
      </div>
    </DesktopLayout>
  );
}

/** Compact summary banner shown to business roles + super_admin:
 * - Days Left for Despatch (from estimated_delivery_date)
 * - Present Status (current stage label)
 * - Advanced (amount of advance required by Accounts)
 * - Received (payment.amount once verified)
 * - Balance to be received (grand_total - received)
 * Non-business/shop-floor roles do not see money numbers — banner still shows
 * Days Left + Present Status. */
function ProjectSummaryBanner({ p, user }) {
  const role = user?.role;
  const showMoney = ["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "dealer"].includes(role);
  const edd = p.estimated_delivery_date;
  let daysLeft = null;
  if (edd) {
    const target = new Date(`${edd}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    daysLeft = Math.round((target - today) / (1000 * 60 * 60 * 24));
  }
  const stageLabel = p.workflow_status || STAGE_LABELS[p.stage] || p.stage;
  const payment = p.payment || {};
  const grandTotal = ((p.quote_snapshot || {}).totals || {}).grand_total || null;
  const received = payment.advance_received ? (payment.amount ?? payment.advance_required ?? 0) : 0;
  const balance = grandTotal != null ? Math.max(0, grandTotal - (received || 0)) : null;
  const fmtMoney = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);
  const daysColor = daysLeft == null ? "text-slate-500"
    : daysLeft < 0 ? "text-red-700"
    : daysLeft <= 7 ? "text-amber-700"
    : "text-emerald-700";
  return (
    <section className="hk-card p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="project-summary-banner">
      <BannerTile
        icon={<CalendarClock className="w-4 h-4" />}
        label="Days left for despatch"
        value={
          daysLeft == null ? "—" :
          daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` :
          daysLeft === 0 ? "Today" : `${daysLeft} days`
        }
        valueClass={daysColor}
        testid="banner-days-left"
      />
      <BannerTile
        icon={<Activity className="w-4 h-4" />}
        label="Present status"
        value={stageLabel}
        valueClass="text-slate-900"
        testid="banner-present-status"
      />
      {showMoney ? (
        <>
          <BannerTile
            icon={<Wallet className="w-4 h-4" />}
            label="Received"
            value={fmtMoney(received || 0)}
            valueClass={received > 0 ? "text-emerald-700" : "text-slate-500"}
            testid="banner-received"
          />
          <BannerTile
            icon={<Wallet className="w-4 h-4" />}
            label="Balance to be received"
            value={fmtMoney(balance)}
            valueClass={balance && balance > 0 ? "text-amber-700" : "text-emerald-700"}
            testid="banner-balance"
          />
        </>
      ) : (
        <div className="col-span-2 flex items-center text-[11px] text-slate-500 italic">
          Financial details visible to business roles only.
        </div>
      )}
    </section>
  );
}

function BannerTile({ icon, label, value, valueClass, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="hk-tiny-label flex items-center gap-1 mb-1 text-slate-500">
        {icon} <span>{label}</span>
      </div>
      <div className={`text-lg font-black tracking-tight truncate ${valueClass || ""}`} style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
    </div>
  );
}

function QuoteSnapshotCard({ snapshot, isBusiness, pc, compact = false }) {
  const lines = snapshot?.lines || [];
  return (
    <section className="hk-card p-4 sm:p-5" data-testid="snapshot-card">
      <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
        <h2 className="hk-tiny-label">Modules & Accessories</h2>
        <span className="text-[10px] text-slate-500">Snapshot from quote {snapshot?.quote_number || "—"}</span>
      </div>
      {(pc?.cabinet_color || pc?.door_color) && (
        <div className="grid grid-cols-2 gap-3 text-xs mb-3">
          <div><span className="hk-tiny-label">Cabinet colour: </span><b>{pc.cabinet_color || "—"}</b></div>
          <div><span className="hk-tiny-label">Door colour: </span><b>{pc.door_color || "—"}</b></div>
        </div>
      )}
      {compact ? (
        <ul className="space-y-2">
          {lines.length === 0 && (
            <li className="text-xs text-slate-400 text-center py-4">No modules on snapshot</li>
          )}
          {lines.map((l, i) => (
            <li key={i} className="border border-slate-200 rounded-sm p-2 bg-white" data-testid={`snapshot-line-${i}`}>
              <div className="font-semibold text-slate-800 text-sm truncate">{l.module_name}</div>
              <div className="text-[11px] text-slate-500 truncate">
                {l.cabinet_type || "—"} · {l.material_name || "—"}
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px]">
                <div><span className="text-slate-500">Door:</span> <b>{l.door_type_name || "—"}</b></div>
                <div><span className="text-slate-500">Handle:</span> <b>{l.handle_name || "—"}</b></div>
                <div><span className="text-slate-500">W×D×H:</span> <b>{l.width || "—"}×{l.depth || "—"}×{l.height || "—"}</b></div>
                <div><span className="text-slate-500">Qty:</span> <b>{l.quantity || l.qty || 1}</b></div>
              </div>
              {(l.accessories || []).length > 0 && (
                <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                  <div className="text-[10px] font-black uppercase text-slate-500 mb-1">Accessories</div>
                  <ul className="space-y-0.5">
                    {(l.accessories || []).map((a, j) => (
                      <li key={j} className="text-[11px] text-slate-700 truncate">
                        {a.accessory_name || a.name}{" "}
                        <span className="text-slate-400">· {a.brand || "—"} · {a.model_number || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {isBusiness && (
                <div className="text-[11px] font-mono text-right mt-1">{l.line_total ?? "—"}</div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="hk-tiny-label bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">Module</th>
                <th className="px-3 py-2 text-left">Cabinet</th>
                <th className="px-3 py-2 text-left">Material</th>
                <th className="px-3 py-2 text-left">Door</th>
                <th className="px-3 py-2 text-left">Handle</th>
                <th className="px-3 py-2">W×D×H</th>
                <th className="px-3 py-2">Qty</th>
                <th className="px-3 py-2 text-left">Accessories</th>
                {isBusiness && <th className="px-3 py-2 text-right">Line total</th>}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">No modules on snapshot</td></tr>
              ) : lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2 font-semibold">{l.module_name}</td>
                  <td className="px-3 py-2 text-xs">{l.cabinet_type || "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.material_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.door_type_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.handle_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.width || "—"} × {l.depth || "—"} × {l.height || "—"}</td>
                  <td className="px-3 py-2 text-xs text-center">{l.quantity || l.qty || 1}</td>
                  <td className="px-3 py-2 text-xs">{(l.accessories || []).map((a, j) => (
                    <div key={j}>{a.accessory_name || a.name} <span className="text-slate-400">· {a.brand || "—"} · {a.model_number || "—"}</span></div>
                  ))}</td>
                  {isBusiness && <td className="px-3 py-2 text-xs text-right font-mono">{l.line_total ?? "—"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TimelineCard({ timeline }) {
  return (
    <section className="hk-card p-4 sm:p-5 overflow-hidden" data-testid="timeline-card">
      <h2 className="hk-tiny-label mb-3">Activity</h2>
      <ol className="space-y-3">
        {(timeline || []).slice().reverse().slice(0, 30).map((t, i) => (
          <li key={i} className="flex items-start gap-3 text-sm">
            <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="[overflow-wrap:anywhere] break-words"><b>{t.actor_name}</b> <span className="text-slate-500">· {t.action.replace(/_/g, " ")}</span>
                {t.to_stage && <span className="text-slate-500"> → {STAGE_LABELS[t.to_stage] || t.to_stage}</span>}</div>
              {t.note && <div className="text-xs text-slate-500 mt-0.5 [overflow-wrap:anywhere] break-words">{t.note}</div>}
              {t.reason && <div className="text-xs text-red-700 mt-0.5 [overflow-wrap:anywhere] break-words">Reason: {t.reason}</div>}
              <div className="text-[11px] text-slate-400 mt-0.5">{new Date(t.at).toLocaleString()}</div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function IssuesCard({ projectId, projectPid }) {
  const { user } = useAuth();
  const [issues, setIssues] = useState([]);
  const [show, setShow] = useState(false);
  const [state, setState] = useState({ description: "", assigned_to_role: "", tag: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/project/issues", { params: { project_id: projectId } }); setIssues(data); }
    catch (_) { /* noop */ }
  };
  useEffect(() => { load(); }, [projectId]);
  const submit = async (e) => {
    e.preventDefault();
    if (!state.description.trim()) return toast.error("Description required");
    setBusy(true);
    try {
      await api.post("/project/issues", { ...state, project_id: projectId });
      toast.success("Issue raised"); setState({ description: "", assigned_to_role: "", tag: "" });
      setShow(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const resolve = async (iid) => {
    const res = prompt("Resolution:"); if (!res) return;
    await api.post(`/project/issues/${iid}/resolve`, { resolution: res }); load();
  };
  return (
    <section className="hk-card p-4 sm:p-5" data-testid="issues-card">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="hk-tiny-label flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Issues ({issues.length})</h2>
        <button onClick={() => setShow((v) => !v)} data-testid="issue-toggle"
          className={`text-xs font-black uppercase px-3 py-1.5 rounded-sm ${show ? "bg-slate-200 text-slate-700 hover:bg-slate-300" : "bg-orange-500 text-white hover:bg-orange-600"}`}>{show ? "Cancel" : "+ Raise issue"}</button>
      </div>
      {show && (
        <form onSubmit={submit} className="mb-4 space-y-2">
          <textarea className={FIELD} rows={3} placeholder="Describe the issue"
            value={state.description} onChange={(e) => setState({ ...state, description: e.target.value })} data-testid="issue-desc" />
          <div className="grid grid-cols-2 gap-2">
            <select className={FIELD} value={state.assigned_to_role} onChange={(e) => setState({ ...state, assigned_to_role: e.target.value })} data-testid="issue-assignee">
              <option value="">— Assign to team —</option>
              {[
                "super_admin", "sales", "sales_coordination", "accounts",
                "production_design", "production_coordination", "production",
                "installation", "dealer",
              ].map((r) => (
                <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
              ))}
            </select>
            <select className={FIELD} value={state.tag} onChange={(e) => setState({ ...state, tag: e.target.value })}>
              <option value="">Tag (optional)</option>
              <option value="rework">Rework</option>
              <option value="materials">Materials</option>
              <option value="info">Information</option>
            </select>
          </div>
          <button type="submit" disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm disabled:opacity-60" data-testid="issue-submit">
            {busy ? "Saving…" : "Raise issue"}
          </button>
        </form>
      )}
      {issues.length === 0 ? (
        <div className="text-sm text-slate-500">No issues on this project.</div>
      ) : (
        <ul className="space-y-2">
          {issues.map((i) => (
            <li key={i.id} className={`border rounded-sm p-3 text-sm ${i.status === "resolved" ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900">{i.description}</div>
                  <div className="text-xs text-slate-500 mt-0.5">by {i.raised_by_name} · {i.assigned_to_role ? `→ ${i.assigned_to_role}` : "unassigned"}{i.tag ? ` · ${i.tag}` : ""}</div>
                  {i.resolution && <div className="text-xs text-emerald-700 mt-1">✓ {i.resolution}</div>}
                </div>
                {i.status === "open" && (
                  <button onClick={() => resolve(i.id)} className="text-xs text-emerald-700 font-bold shrink-0">Resolve</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ProcurementCard({ p, act, user }) {
  const proc = p.procurement || {};
  // Item 12: Procurement STATUS is visible to everyone (design, sales, production…).
  // Only Accounts/Admin/Super Admin can UPDATE it (transition status forward).
  const canEdit = ["super_admin", "admin", "accounts"].includes(user?.role);
  const [notes, setNotes] = useState("");
  // Hide only during the earliest stages when procurement hasn't been started yet
  // (payment_verification, customer_approval) AND status hasn't been changed.
  if ((p.stage === "payment_verification" || p.stage === "customer_approval") && (proc.status || "not_started") === "not_started") return null;
  const nextStatus = { not_started: "in_process", in_process: "received" };
  const next = nextStatus[proc.status || "not_started"];
  const statusLabels = {
    not_started: "Not started", in_process: "In process", received: "Received",
  };
  return (
    <div className="hk-card p-4" data-testid="proc-card">
      <div className="hk-tiny-label mb-2 flex items-center justify-between">
        <span>Procurement (parallel)</span>
        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-sm ${
          proc.status === "received" ? "bg-emerald-100 text-emerald-800" :
          proc.status === "in_process" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}
          data-testid="proc-status">
          {statusLabels[proc.status] || "Not started"}
        </span>
      </div>
      {(proc.notes || proc.started_at || proc.received_at) && (
        <div className="text-xs text-slate-600 space-y-0.5 mb-2">
          {proc.started_at && <div>Started: {new Date(proc.started_at).toLocaleDateString()}</div>}
          {proc.received_at && <div>Received: {new Date(proc.received_at).toLocaleDateString()}</div>}
          {proc.notes && <div className="text-slate-500">Note: {proc.notes}</div>}
        </div>
      )}
      {canEdit ? (
        next ? (
          <>
            <textarea className={FIELD} rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="proc-notes" />
            <button
              onClick={() => act(() => api.post(`/project/${p.id}/procurement`, { status: next, notes }))}
              className="mt-2 w-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-3 py-2 rounded-sm text-xs"
              data-testid="proc-next">
              Mark {statusLabels[next]}
            </button>
          </>
        ) : (
          <p className="text-[11px] text-emerald-700">All items received.</p>
        )
      ) : (
        <p className="text-[11px] text-slate-500 italic">View only. Accounts / Admin can update.</p>
      )}
    </div>
  );
}

function StageActions({ p, act, busy, user }) {
  const role = user?.role;
  // Strict rule: only the stage owner (or super_admin) can act. Everyone else
  // sees a read-only note. This blocks non-owners from advancing / mutating stage.
  if (p.stage === "payment_verification" && role === "accounts") {
    return <PaymentPanel p={p} act={act} />;
  }
  if (p.stage === "design_drawings" && ["super_admin", "production_design"].includes(role)) {
    return <DesignPanel p={p} act={act} />;
  }
  if (p.stage === "customer_approval" && ["super_admin", "sales_coordination", "sales"].includes(role)) {
    // For sales role, only the ASSIGNED sales person (or the project creator)
    // can see the panel — enforced on the backend too.
    if (role === "sales") {
      const isOwner = p.assigned_sales_id === user?.id || p.sales_person_id === user?.id || p.created_by === user?.id;
      if (!isOwner) {
        return <div className="hk-card p-4 text-xs text-slate-500">Stage owner: Sales Coord — view only for you.</div>;
      }
    }
    return <CustomerApprovalPanel p={p} act={act} />;
  }
  if (p.stage === "production_coordination" && ["super_admin", "production_coordination"].includes(role)) {
    return <ProdCoordPanel p={p} act={act} />;
  }
  // Production stage — render the enhanced ProductionPanel with per-station
  // status controls, In Queue #N chip, per-station photo uploads, and the
  // sequential-activation lock. Installation users also see it (they own the
  // stations flagged owner_role="installation").
  if (p.stage === "production" && ["super_admin", "production", "production_coordination", "installation"].includes(role)) {
    return <ProductionPanel p={p} act={act} user={user} />;
  }
  if (p.stage === "pre_installation" && ["super_admin", "production"].includes(role)) {
    return <PreInstallPanel p={p} act={act} />;
  }
  if (p.stage === "dispatch" && ["super_admin", "sales"].includes(role)) {
    return <DispatchPanel p={p} act={act} />;
  }
  if (p.stage === "installation" && ["super_admin", "installation"].includes(role)) {
    // For installation stage, still show the ProductionPanel so the crew can
    // tick off the installation-owned stations (Site Installation, Final QC,
    // Handover).
    return <ProductionPanel p={p} act={act} user={user} />;
  }
  if (p.stage === "review_closeout" && ["super_admin", "sales"].includes(role)) {
    return <ReviewCloseoutPanel p={p} act={act} />;
  }
  const ownerRole = p.stage_owner_role || STAGE_OWNER_ROLE[p.stage];
  return (
    <div className="hk-card p-4" data-testid="stage-readonly-note">
      <div className="hk-tiny-label mb-1">Stage owner</div>
      <div className="text-sm text-slate-700">
        This stage is owned by <b>{ownerRole ? (ROLE_LABEL[ownerRole] || ownerRole) : "another team"}</b>.
        You have read-only access.
      </div>
    </div>
  );
}

function PaymentPanel({ p, act }) {
  const [amt, setAmt] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <div className="hk-card p-4" data-testid="payment-panel">
      <div className="hk-tiny-label mb-2">Payment Verification</div>
      <p className="text-xs text-slate-600 mb-3">Advance required: <b>{p.payment?.advance_required || "—"}</b></p>
      <input type="number" className={FIELD} placeholder="Amount received" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="pay-amount" />
      <input type="text" className={`${FIELD} mt-2`} placeholder="UTR / notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <button onClick={() => act(() => api.post(`/project/${p.id}/payment/advance`, { amount: parseFloat(amt) || null, notes }), "Advance verified")}
        className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="pay-verify">
        Advance received → Design
      </button>
    </div>
  );
}

function DesignPanel({ p, act }) {
  const [uploading, setUploading] = useState(false);
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const form = new FormData(); form.append("file", f);
        await api.post(`/project/${p.id}/files`, form, {
          params: { category: "production_design" },
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Production designs uploaded");
      await act(async () => {}, null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const uploaded = (p.attachments || []).filter((a) => (a.category || "") === "production_design");
  return (
    <div className="hk-card p-4" data-testid="design-panel">
      <div className="hk-tiny-label mb-2">Upload production designs</div>
      <p className="text-xs text-slate-600 mb-3">Upload the finalised 2D / 3D production drawings. When ready, send them to Sales for approval.</p>
      <label className="flex items-center justify-center gap-1 border-2 border-dashed border-slate-300 rounded-sm p-3 cursor-pointer hover:border-orange-500 text-xs" data-testid="design-upload">
        <input type="file" hidden multiple onChange={(e) => uploadFiles(e.target.files)} />
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        {uploading ? "Uploading…" : "Choose production design file(s)"}
      </label>
      {uploaded.length > 0 && (
        <div className="text-[11px] text-slate-500 mt-2" data-testid="design-uploaded-count">
          {uploaded.length} design file{uploaded.length === 1 ? "" : "s"} uploaded
        </div>
      )}
      <button onClick={() => act(() => api.post(`/project/${p.id}/design/ready`), "Sent to Sales for approval")}
        disabled={uploaded.length === 0 || uploading}
        title={uploaded.length === 0 ? "Upload at least one production design file first" : ""}
        className="mt-3 w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed disabled:hover:bg-slate-300 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="design-send-to-sales">
        Send to Sales for approval →
      </button>
      {uploaded.length === 0 && (
        <p className="text-[11px] text-slate-500 mt-1.5 text-center" data-testid="design-send-hint">
          Upload at least one file to enable this button.
        </p>
      )}
    </div>
  );
}

function CustomerApprovalPanel({ p, act }) {
  const [method, setMethod] = useState("email");
  const [notes, setNotes] = useState("");
  return (
    <div className="hk-card p-4" data-testid="approval-panel">
      <div className="hk-tiny-label mb-2">Customer Approval</div>
      <select className={FIELD} value={method} onChange={(e) => setMethod(e.target.value)}>
        <option value="email">Email approval</option>
        <option value="whatsapp">WhatsApp</option>
        <option value="signed_drawing">Signed drawing</option>
      </select>
      <textarea className={`${FIELD} mt-2`} rows={2} placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <button onClick={() => act(() => api.post(`/project/${p.id}/customer-approval`, { state: "approved", method, notes }), "Approved")}
        className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="approval-approve">
        Customer approved → Production Coord
      </button>
      <button onClick={() => act(() => api.post(`/project/${p.id}/customer-approval`, { state: "rejected", method, notes }), "Rejected — back to design")}
        className="mt-2 w-full border border-red-300 text-red-700 hover:bg-red-50 font-bold px-4 py-2 rounded-sm text-sm">
        Rejected — back to design
      </button>
    </div>
  );
}

function ProdCoordPanel({ p, act }) {
  const pc = p.production_coordination || {};
  const [cab, setCab] = useState(pc.cabinet_color || "");
  const [door, setDoor] = useState(pc.door_color || "");
  const [extras, setExtras] = useState((pc.extra_materials || []).map((e) => `${e.name} · ${e.qty} ${e.unit || ""}`).join("\n"));
  const save = async () => {
    const parsed = extras.split("\n").filter(Boolean).map((line) => {
      const [name, qty, unit] = line.split("·").map((s) => (s || "").trim());
      return { name, qty: Number(qty) || 1, unit: unit || "" };
    });
    await act(() => api.post(`/project/${p.id}/prod-coord`, { cabinet_color: cab, door_color: door, extra_materials: parsed }), "Saved");
  };
  return (
    <div className="hk-card p-4 space-y-2" data-testid="prod-coord-panel">
      <div className="hk-tiny-label mb-1">Production Coordination</div>
      <input className={FIELD} placeholder="Cabinet colour" value={cab} onChange={(e) => setCab(e.target.value)} data-testid="pc-cab" />
      <input className={FIELD} placeholder="Door colour" value={door} onChange={(e) => setDoor(e.target.value)} data-testid="pc-door" />
      <textarea className={FIELD} rows={3} placeholder={"Extra materials, one per line:\nScrews · 200 · pcs"} value={extras} onChange={(e) => setExtras(e.target.value)} data-testid="pc-extras" />
      <button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="pc-save">Save</button>
      <button onClick={() => act(() => api.post(`/project/${p.id}/prod-coord/handoff`), "Handed off → Production")}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="pc-handoff">
        Hand off to Production →
      </button>
    </div>
  );
}

function ProductionPanel({ p, act, user }) {
  const allStations = React.useMemo(
    () => [...((p.production || {}).stations || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [p.production]
  );
  // First non-completed station across the FULL list — everything after
  // is locked. Locking must respect the whole sequence, not just the
  // scoped view, otherwise Installation would see Site Installation
  // before Production wraps up.
  const activeIdxFull = allStations.findIndex((s) => s.status !== "completed");
  // iter 89 — role-scoped view: production users only see production-
  // owned stations; installation users only see installation-owned. All
  // other roles (super_admin, prod_coord, sales, accounts) still see the
  // full station list for oversight.
  const stations = React.useMemo(() => {
    if (!user) return allStations;
    if (user.role === "production")   return allStations.filter((s) => (s.owner_role || "production") === "production");
    if (user.role === "installation") return allStations.filter((s) => s.owner_role === "installation");
    return allStations;
  }, [allStations, user]);
  const done = stations.filter((s) => s.status === "completed").length;
  const canEdit = (s) => {
    if (!user) return false;
    if (["super_admin", "production_coordination"].includes(user.role)) return true;
    const need = s.owner_role || "production";
    return user.role === need;
  };
  return (
    <div className="hk-card p-4" data-testid="production-panel">
      <div className="hk-tiny-label mb-2 flex items-center justify-between">
        <span>{user?.role === "installation" ? "Installation stations"
              : user?.role === "production"   ? "Production stations"
              : "Production + Installation stations"}</span>
        <span className="text-xs">{done}/{stations.length} done</span>
      </div>
      <ul className="space-y-2 text-sm">
        {stations.map((s) => {
          const iFull = allStations.indexOf(s);
          const isActive = iFull === activeIdxFull;
          const locked = !isActive && s.status !== "completed";
          const editable = canEdit(s) && !locked;
          const st = s.status || "pending";
          const rowBg = st === "completed" ? "bg-emerald-50 border-emerald-200"
            : st === "in_progress" ? "bg-blue-50 border-blue-200"
            : st === "in_queue" ? "bg-amber-50 border-amber-200"
            : st === "on_hold" || st === "info_required" ? "bg-red-50 border-red-200"
            : locked ? "bg-slate-50 border-slate-200 opacity-70"
            : "bg-white border-slate-200";
          return (
            <li key={s.key} className={`border rounded-sm ${rowBg}`} data-testid={`station-${s.key}`}>
              <div className="px-2 py-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-black text-slate-400">#{s.order}</span>
                      <span className="truncate">{s.name}</span>
                      <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded-sm bg-slate-100 text-slate-500">
                        {(s.owner_role || "production").replace("_", " ")}
                      </span>
                    </div>
                    <StationStatusChip station={s} queuePos={p.queue_position} isActive={isActive} />
                  </div>
                  <div className="shrink-0">
                    {locked && <span className="text-[10px] font-bold text-slate-400 uppercase">locked</span>}
                    {st === "completed" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </div>
                </div>
                {editable && st !== "completed" && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-2">
                    <button onClick={() => act(() => api.post(`/project/${p.id}/production/station/${s.key}`, { status: "in_queue" }))}
                      className="text-[11px] font-black uppercase text-white bg-amber-500 hover:bg-amber-600 border-2 border-amber-700 px-2 py-2 rounded-sm shadow-sm"
                      data-testid={`st-queue-${s.key}`}>Queue</button>
                    <button onClick={() => act(() => api.post(`/project/${p.id}/production/station/${s.key}`, { status: "in_progress" }))}
                      className="text-[11px] font-black uppercase text-white bg-blue-600 hover:bg-blue-700 border-2 border-blue-800 px-2 py-2 rounded-sm shadow-sm"
                      data-testid={`st-start-${s.key}`}>Start</button>
                    <button onClick={() => act(() => api.post(`/project/${p.id}/production/station/${s.key}`, { status: "completed" }))}
                      className="text-[11px] font-black uppercase text-white bg-emerald-600 hover:bg-emerald-700 border-2 border-emerald-800 px-2 py-2 rounded-sm shadow-sm"
                      data-testid={`st-done-${s.key}`}>Done</button>
                    {isDispatchStation(allStations, s) ? (
                      <button onClick={() => {
                        if (!window.confirm("Are you sure this project is completed?\n\nDispatching marks every remaining station complete and moves the project to Finished Projects.")) return;
                        act(() => api.post(`/project/${p.id}/production/station/${s.key}/dispatch`));
                      }} className="text-[11px] font-black uppercase text-white bg-slate-900 hover:bg-black border-2 border-slate-900 px-2 py-2 rounded-sm shadow-sm"
                        data-testid={`st-dispatch-${s.key}`}>Dispatched</button>
                    ) : (
                      <button onClick={() => {
                        const reason = prompt("Hold reason:"); if (!reason) return;
                        act(() => api.post(`/project/${p.id}/production/station/${s.key}`, { status: "on_hold", reason }));
                      }} className="text-[11px] font-black uppercase text-white bg-red-600 hover:bg-red-700 border-2 border-red-800 px-2 py-2 rounded-sm shadow-sm"
                        data-testid={`st-hold-${s.key}`}>Hold</button>
                    )}
                  </div>
                )}
              </div>
              {(s.status && s.status !== "pending") && (
                <StationPhotos p={p} station={s} act={act} editable={editable} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Every station ordered AFTER "Finishing" carries Dispatched instead of Hold.
function isDispatchStation(allStations, s) {
  const gate = (allStations || []).find(
    (x) => (x.key || "").toLowerCase() === "finishing" || (x.name || "").trim().toLowerCase() === "finishing");
  if (!gate) return false;
  return (s.order || 0) > (gate.order || 0);
}

function StationStatusChip({ station, queuePos, isActive }) {  const st = station.status || "pending";
  if (st === "completed") return <div className="text-[10px] text-emerald-700 mt-0.5">Completed{station.completed_at ? ` · ${new Date(station.completed_at).toLocaleDateString()}` : ""}</div>;
  if (st === "in_progress") return <div className="text-[10px] text-blue-700 mt-0.5">In Progress · started {station.started_at ? new Date(station.started_at).toLocaleDateString() : "—"}</div>;
  if (st === "in_queue") return <div className="text-[10px] text-amber-800 mt-0.5">In Queue{queuePos ? ` #${queuePos}` : ""}</div>;
  if (st === "on_hold") return <div className="text-[10px] text-red-700 mt-0.5">On Hold · {station.hold_reason || ""}</div>;
  if (st === "info_required") return <div className="text-[10px] text-red-700 mt-0.5">Info Required · {station.info_reason || ""}</div>;
  return <div className="text-[10px] text-slate-500 mt-0.5">{isActive ? "Active — set status →" : "Pending"}</div>;
}

function StationPhotos({ p, station, act, editable }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const photos = station.photos || [];
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/project/${p.id}/production/station/${station.key}/photo`, fd,
                       { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      act(() => Promise.resolve());
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="px-2 pb-2 pt-1 border-t border-slate-200/60">
      <div className="flex flex-wrap gap-1.5 items-center">
        {photos.map((ph) => (
          <a key={ph.id} href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/project/${p.id}/files/${ph.id}`}
            target="_blank" rel="noreferrer" title={ph.name}
            className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-700"
            data-testid={`station-photo-${ph.id}`}>
            📷 {ph.name.length > 18 ? ph.name.slice(0, 15) + "…" : ph.name}
          </a>
        ))}
        {editable && (
          <>
            <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden"
              data-testid={`station-photo-input-${station.key}`} />
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
              className="text-[10px] font-bold text-slate-600 hover:text-orange-600 px-1.5 py-0.5 border border-dashed border-slate-300 rounded-sm"
              data-testid={`station-photo-btn-${station.key}`}>
              {uploading ? "Uploading…" : "+ Photo"}
            </button>
          </>
        )}
        {!editable && photos.length === 0 && (
          <span className="text-[10px] text-slate-400 italic">No photos yet</span>
        )}
      </div>
    </div>
  );
}

function DispatchPanel({ p, act }) {
  const d = p.dispatch || {};
  const [notes, setNotes] = useState("");
  if (d.decision === "approved") {
    return (
      <div className="hk-card p-4" data-testid="dispatch-panel">
        <div className="hk-tiny-label mb-2">Dispatch Approval</div>
        <div className="text-xs text-emerald-700 mb-3">✓ Approved by {d.approved_by ? "sales" : "you"}</div>
        <button onClick={() => act(() => api.post(`/project/${p.id}/dispatch/sent`), "Dispatched")}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="dispatch-sent">
          Mark Dispatched → Installation
        </button>
      </div>
    );
  }
  return (
    <div className="hk-card p-4" data-testid="dispatch-panel">
      <div className="hk-tiny-label mb-2">Dispatch Approval</div>
      <textarea className={FIELD} rows={2} placeholder="Notes / QC comments" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <button onClick={() => act(() => api.post(`/project/${p.id}/dispatch/decide`, { approved: true, notes }), "Approved")}
        className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="dispatch-approve">
        Approve for Dispatch
      </button>
      <button onClick={() => act(() => api.post(`/project/${p.id}/dispatch/decide`, { approved: false, notes: notes || "Rejected" }), "Rejected — back to production")}
        className="mt-2 w-full border border-red-300 text-red-700 hover:bg-red-50 font-bold px-4 py-2 rounded-sm text-sm" data-testid="dispatch-reject">
        Reject → Production
      </button>
    </div>
  );
}

function InstallationPanel({ p, act }) {
  const cl = (p.installation || {}).checklist || [];
  const done = cl.filter((c) => c.done).length;
  return (
    <div className="hk-card p-4" data-testid="install-panel">
      <div className="hk-tiny-label mb-2 flex items-center justify-between">
        <span>Installation checklist</span>
        <span className="text-xs">{done}/{cl.length}</span>
      </div>
      <ul className="space-y-1">
        {cl.map((c, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!c.done} onChange={(e) => act(() => api.post(`/project/${p.id}/install/check`, { item_index: i, done: e.target.checked }))} className="w-4 h-4 accent-orange-500" data-testid={`cl-${i}`} />
            <span className={c.done ? "line-through text-slate-400" : ""}>{c.item}</span>
          </li>
        ))}
      </ul>
      {done === cl.length && cl.length > 0 && (
        <button onClick={() => act(() => api.post(`/project/${p.id}/install/complete`, { notes: "" }), "Installation complete")}
          className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="install-complete">
          Installation Complete →
        </button>
      )}
    </div>
  );
}

function ReviewCloseoutPanel({ p, act }) {
  const r = p.review || {};
  const [link, setLink] = useState(r.token ? `${window.location.origin}/review/${r.token}` : "");
  const sendLink = async () => {
    try {
      const { data } = await api.post(`/project/${p.id}/review/send`);
      const full = `${window.location.origin}${data.public_url}`;
      setLink(full);
      navigator.clipboard.writeText(full);
      toast.success("Review link copied to clipboard");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="hk-card p-4" data-testid="review-panel">
      <div className="hk-tiny-label mb-2">Review & Closeout</div>
      <button onClick={sendLink} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="review-send">
        <Send className="w-3.5 h-3.5 inline mr-1" /> Generate / copy review link
      </button>
      {link && <div className="text-[11px] text-slate-500 mt-2 break-all">{link}</div>}
      {r.submitted_at && (
        <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 rounded-sm p-2">
          ✓ Review submitted<br />
          Product Quality: <b>{r.product_quality}/5</b> · Delivery: <b>{r.delivery_time}/5</b> · Coord: <b>{r.coordination}/5</b> · Install: <b>{r.installation}/5</b>
          {r.comments && <div className="mt-1">&ldquo;{r.comments}&rdquo;</div>}
        </div>
      )}
      <button onClick={() => act(() => api.post(`/project/${p.id}/close`), "Project closed")}
        className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="review-close">
        Close project
      </button>
    </div>
  );
}

function PreInstallPanel({ p, act }) {
  const [note, setNote] = useState("");
  return (
    <div className="hk-card p-4" data-testid="pre-install-panel">
      <div className="hk-tiny-label mb-2">Pre-Installation QC</div>
      <p className="text-xs text-slate-600 mb-3">Confirm pre-installation checks are complete before handing off to Sales for dispatch approval.</p>
      <textarea className={FIELD} rows={2} placeholder="Optional QC notes" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pre-install-note" />
      <button onClick={() => act(() => api.post(`/project/${p.id}/pre-install/complete`, { note }), "Pre-installation complete")}
        className="mt-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm" data-testid="pre-install-complete">
        Complete → Packing & Dispatch
      </button>
    </div>
  );
}


/** Item 6 — Special Requirements shown on every project stage; editable
 * up to and including Prod-Coord stage. Read-only after that. */
function SpecialRequirementsCard({ p, act, user }) {
  const [text, setText] = useState(p.special_requirements || "");
  const [editing, setEditing] = useState(false);
  const editableStages = new Set(["payment_verification", "design_drawings", "customer_approval", "production_coordination"]);
  const canEditRole = ["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "admin", "design", "production_design"].includes(user?.role);
  const canEdit = canEditRole && editableStages.has(p.stage);
  return (
    <div className="hk-card p-4" data-testid="special-req-card">
      <div className="hk-tiny-label mb-2 flex items-center justify-between">
        <span>Special requirements</span>
        {canEdit && !editing && (
          <button onClick={() => { setText(p.special_requirements || ""); setEditing(true); }} className="text-xs text-orange-600 hover:text-orange-700" data-testid="special-req-edit">Edit</button>
        )}
      </div>
      {editing ? (
        <>
          <textarea className={FIELD} rows={4} placeholder="e.g. Left-handed handles; matte finish only; delivery not before 5 pm"
            value={text} onChange={(e) => setText(e.target.value)} data-testid="special-req-input" />
          <div className="flex gap-2 mt-2">
            <button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold px-3 py-2 rounded-sm text-xs" data-testid="special-req-save"
              onClick={() => act(async () => {
                await api.patch(`/project/${p.id}`, { special_requirements: text });
                setEditing(false);
              }, "Requirements updated")}>Save</button>
            <button className="border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-xs"
              onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </>
      ) : p.special_requirements ? (
        <div className="text-sm whitespace-pre-wrap bg-amber-50 border border-amber-200 rounded-sm p-2" data-testid="special-req-value">
          {p.special_requirements}
        </div>
      ) : (
        <div className="text-xs text-slate-500 italic">
          {canEdit ? "None specified. Click Edit to add." : "None specified."}
        </div>
      )}
      {!editableStages.has(p.stage) && p.special_requirements && (
        <div className="text-[10px] text-slate-500 mt-2">Locked after Production Coordination.</div>
      )}
    </div>
  );
}

/** Red banner surfacing the currently-open issue on this project. Displays
 * the tag, description snippet, and the assigned owner. Clicking scrolls the
 * user to the Issues card. */
function ActiveIssueBanner({ p, onOpen }) {
  const desc = (p.sub_status_reason || "").replace(/^([^:]+:\s*)/, "");
  return (
    <div
      role="button"
      onClick={onOpen}
      data-testid="active-issue-banner"
      className="mt-3 rounded-sm border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-red-100 transition-colors"
    >
      <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-sm bg-red-600 text-white">
            {p.active_issue_tag || "Issue"}
          </span>
          <span className="text-xs font-black text-red-800">Open issue on this project</span>
        </div>
        {desc && (
          <div className="text-sm text-red-900 mt-1 line-clamp-2" data-testid="active-issue-desc">
            {desc}
          </div>
        )}
        <div className="text-[11px] text-red-800 mt-1.5">
          Currently owned by <b data-testid="active-issue-owner">
            {p.active_issue_owner_name
              || (p.active_issue_owner_role
                  ? p.active_issue_owner_role.replace(/_/g, " ")
                  : "— Unassigned —")}
          </b>
        </div>
      </div>
      <div className="hidden sm:block text-[10px] font-black uppercase text-red-700 shrink-0 self-center">
        View issue →
      </div>
    </div>
  );
}


/** Sales-side shortcut card that lets Sales / Sales Coord / Super Admin fire
 * off an additional 2D/3D drawing request for the linked enquiry, WITHOUT
 * having to open the enquiry page. Under the hood this hits
 * `POST /api/project/{pid}/request-drawings` which:
 *   • forwards to the enquiry's `design_brief` when the project has a linked
 *     enquiry (iter65 behaviour — enquiry stage stays `converted`), OR
 *   • synthesises a shim "converted" enquiry (using customer name + project
 *     name from the quote) and back-links it to the project when the project
 *     was created via the direct-quote → project flow.
 * Visible to Sales / Sales Coord / Super Admin on every project. */
function RequestExtraDrawingsCard({ p, user }) {
  const role = user?.role;
  const canAsk = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  // iter95 — available until the job hits the shop floor; from Production
  // onwards drawing changes must be raised as an issue.
  const stageIdx = PROJECT_STAGES.findIndex((s) => s.key === p.stage);
  const inProduction = stageIdx >= PROJECT_STAGES.findIndex((s) => s.key === "production");
  if (!canAsk || inProduction) return null;
  const submit = async () => {
    const msg = notes.trim();
    if (!msg) { toast.error("Please describe what drawings are needed"); return; }
    setBusy(true);
    try {
      await api.post(`/project/${p.id}/request-drawings`, { note: msg });
      toast.success("Drawing request sent to the 2D/3D team");
      setOpen(false);
      setNotes("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="hk-card p-4" data-testid="request-extra-drawings-card">
      <div className="hk-tiny-label mb-1 text-slate-600 flex items-center gap-1.5">
        <PenTool className="w-3.5 h-3.5" /> Push to 2D/3D Design
      </div>
      {!open ? (
        <>
          <p className="text-[11px] text-slate-500 mb-2">
            Ask the Design team for more drawings on this project&rsquo;s enquiry
            (extra wardrobe, revision, elevation etc.).
          </p>
          <button type="button" data-testid="request-extra-drawings-open"
            onClick={() => setOpen(true)}
            className="w-full text-xs font-black uppercase bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-sm">
            Request drawings
          </button>
        </>
      ) : (
        <>
          <p className="text-[11px] text-slate-500 mb-2">
            Describe what the 2D/3D team should prepare. Design will see this
            note on the enquiry.
          </p>
          <textarea className={FIELD} rows={4}
            placeholder="e.g. Add a 900mm wardrobe elevation for the guest room; keep same finish"
            value={notes} onChange={(ev) => setNotes(ev.target.value)}
            data-testid="request-extra-drawings-notes" />
          <div className="flex gap-2 mt-2">
            <button className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold px-3 py-2 rounded-sm text-xs"
              disabled={busy} onClick={submit}
              data-testid="request-extra-drawings-submit">
              {busy ? "Sending…" : "Send to Design"}
            </button>
            <button className="border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-xs"
              disabled={busy} onClick={() => { setOpen(false); setNotes(""); }}
              data-testid="request-extra-drawings-cancel">
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Send-back button — shown to Super Admin and to the current stage owner
 * only. Any other role (Sales, Dealer, 2D/3D Design, etc.) does not see it. */
function RevertStageButton({ p, act, user }) {
  const role = user?.role;
  const owner = STAGE_OWNER_ROLE[p.stage];
  const canRevert = role === "super_admin" || (owner && role === owner);
  if (!canRevert || p.stage === "payment_verification" || p.stage === "completed") {
    return null;
  }
  return (
    <div className="hk-card p-4" data-testid="revert-stage-card">
      <div className="hk-tiny-label mb-1 text-slate-600">Accidentally advanced?</div>
      <p className="text-[11px] text-slate-500 mb-2">Send this project back to the previous stage.</p>
      <button type="button" data-testid="revert-stage-btn"
        onClick={() => {
          if (!window.confirm("Send this project back to the previous stage?")) return;
          act(() => api.post(`/project/${p.id}/stage/revert`, { note: "" }), "Stage reverted");
        }}
        className="w-full text-xs font-black uppercase border border-slate-300 hover:bg-slate-50 text-slate-800 px-3 py-2 rounded-sm">
        ← Send to previous stage
      </button>
    </div>
  );
}


/** Generic advance button (used for the production stage since the stations panel
 * was removed per user request — Item 10). */
function GenericAdvancePanel({ p, act, label }) {
  return (
    <div className="hk-card p-4" data-testid="generic-advance-panel">
      <div className="hk-tiny-label mb-2">Advance stage</div>
      <p className="text-xs text-slate-600 mb-3">
        Clicking advance marks this stage complete and moves the project forward.
      </p>
      <button onClick={() => act(() => api.post(`/project/${p.id}/stage/advance`), "Advanced")}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm"
        data-testid="advance-stage-btn">
        {label || "Advance to next stage"}
      </button>
    </div>
  );
}


/** Item 4 — Edit project modal.
 * Editable: name, delivery date, special requirements, customer contact, sales rep.
 * Backend gate: PATCH /api/project/{id} accepts super_admin, sales, sales_coord, prod_coord.
 * Special Requirements is auto-locked after prod-coord handoff. */
function EditProjectModal({ p, onClose, onSaved }) {
  const { user } = useAuth();
  const canReassignSales = user?.role === "super_admin";
  const [name, setName] = useState(p.name || "");
  const [edd, setEdd] = useState(p.estimated_delivery_date || "");
  const [special, setSpecial] = useState(p.special_requirements || "");
  const [custName, setCustName] = useState(p.customer_name || "");
  const [custPhone, setCustPhone] = useState(p.customer_phone || "");
  const [custEmail, setCustEmail] = useState(p.customer_email || "");
  const [custAddr, setCustAddr] = useState(p.customer_address || "");
  const [salesId, setSalesId] = useState(p.assigned_sales_id || "");
  const [salesReps, setSalesReps] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!canReassignSales) return;
    (async () => {
      try {
        const { data } = await api.get("/enquiry/sales");
        setSalesReps(data);
      } catch (e) { /* noop */ }
    })();
  }, [canReassignSales]);
  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (name !== p.name) payload.project_name = name.trim();
      if (edd !== (p.estimated_delivery_date || "")) payload.estimated_delivery_date = edd || null;
      if (special !== (p.special_requirements || "")) payload.special_requirements = special;
      if (custName !== (p.customer_name || "")) payload.customer_name = custName;
      if (custPhone !== (p.customer_phone || "")) payload.customer_phone = custPhone;
      if (custEmail !== (p.customer_email || "")) payload.customer_email = custEmail;
      if (custAddr !== (p.customer_address || "")) payload.customer_address = custAddr;
      if (canReassignSales && salesId !== (p.assigned_sales_id || "")) payload.assigned_sales_id = salesId;
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await api.patch(`/project/${p.id}`, payload);
      toast.success("Project updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="edit-project-modal" onClick={onClose}>
      <div className="bg-white rounded-sm shadow-xl w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Edit project</div>
            <h2 className="text-xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>{p.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="edit-close"><XCircle className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <label className="block">
            <div className="hk-tiny-label mb-1">Project name</div>
            <input className={FIELD} value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name" />
          </label>
          <label className="block">
            <div className="hk-tiny-label mb-1">Estimated delivery date</div>
            <input type="date" className={FIELD} value={edd || ""} onChange={(e) => setEdd(e.target.value)} data-testid="edit-edd" />
          </label>
          <label className="block">
            <div className="hk-tiny-label mb-1">
              Sales rep (owner)
              {!canReassignSales && <span className="ml-1 text-[10px] text-slate-400 normal-case font-normal">(Super Admin only)</span>}
            </div>
            <select className={`${FIELD} ${canReassignSales ? "" : "bg-slate-100 cursor-not-allowed"}`}
              disabled={!canReassignSales}
              value={salesId} onChange={(e) => setSalesId(e.target.value)} data-testid="edit-sales">
              {canReassignSales ? (
                <>
                  <option value="">— Unassigned —</option>
                  {salesReps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </>
              ) : (
                <option value={salesId || ""}>{p.assigned_sales_name || "— Unassigned —"}</option>
              )}
            </select>
          </label>
          <label className="block">
            <div className="hk-tiny-label mb-1">Special requirements</div>
            <textarea className={FIELD} rows={3} value={special} onChange={(e) => setSpecial(e.target.value)} data-testid="edit-special" />
          </label>
          <fieldset className="border border-slate-200 rounded-sm p-3">
            <legend className="hk-tiny-label px-1">Customer contact</legend>
            <label className="block mb-2">
              <div className="text-xs text-slate-600 mb-0.5">Name</div>
              <input className={FIELD} value={custName} onChange={(e) => setCustName(e.target.value)} data-testid="edit-cust-name" />
            </label>
            <div className="grid sm:grid-cols-2 gap-2">
              <label className="block">
                <div className="text-xs text-slate-600 mb-0.5">Phone</div>
                <input className={FIELD} value={custPhone} onChange={(e) => setCustPhone(e.target.value)} data-testid="edit-cust-phone" />
              </label>
              <label className="block">
                <div className="text-xs text-slate-600 mb-0.5">Email</div>
                <input className={FIELD} value={custEmail} onChange={(e) => setCustEmail(e.target.value)} data-testid="edit-cust-email" />
              </label>
            </div>
            <label className="block mt-2">
              <div className="text-xs text-slate-600 mb-0.5">Site address</div>
              <textarea className={FIELD} rows={2} value={custAddr} onChange={(e) => setCustAddr(e.target.value)} data-testid="edit-cust-address" />
            </label>
          </fieldset>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-slate-300 hover:bg-slate-50 font-bold text-slate-800 px-4 py-2 rounded-sm text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="edit-save"
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold px-4 py-2 rounded-sm text-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null} Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



/** Files card — 4 categories with per-role upload/delete gates.
 * customer          — view only (super_admin can delete)
 * 2d3d              — sales/coord/design/prod-design upload; sales/coord/super delete
 * production_design — anyone can upload; only super_admin can delete
 * internal          — sales/coord/prod-coord upload; super_admin delete */
function FilesCard({ p, act, user }) {
  const [cat, setCat] = useState("customer");
  const [uploading, setUploading] = useState(false);
  const attachments = (p.attachments || []).filter((a) => (a.category || "internal") === cat);
  const role = user?.role;
  const canUpload = {
    customer: ["super_admin"].includes(role),
    "2d3d": ["super_admin", "sales", "sales_coordination", "design", "production_design"].includes(role),
    production_design: !!role && role !== "dealer", // any internal role may upload
    internal: ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role),
  }[cat];
  const canDelete = (att) => {
    if (role === "super_admin") return true;
    if (cat === "2d3d") return ["sales", "sales_coordination"].includes(role);
    return false;
  };
  const uploadFile = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const form = new FormData(); form.append("file", f);
        await api.post(`/project/${p.id}/files`, form, {
          params: { category: cat },
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Uploaded");
      await act(async () => {}, null); // trigger reload via act
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const del = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    await act(async () => { await api.delete(`/project/${p.id}/files/${fileId}`); }, "File deleted");
  };
  return (
    <div className="hk-card p-4" data-testid="files-card">
      <div className="hk-tiny-label mb-2">Project files</div>
      <div className="grid grid-cols-2 gap-1 mb-2 sm:flex sm:flex-wrap">
        {[["customer", "From Customer"], ["2d3d", "2D / 3D"], ["production_design", "Prod Design"], ["internal", "Internal"]].map(([k, label]) => {
          const n = (p.attachments || []).filter((a) => (a.category || "internal") === k).length;
          return (
            <button type="button" key={k} onClick={() => setCat(k)} data-testid={`files-tab-${k}`}
              className={`text-[10px] font-black uppercase px-2 py-1.5 rounded-sm truncate ${cat === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
              {label} ({n})
            </button>
          );
        })}
      </div>
      {attachments.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid={`files-empty-${cat}`}>No files in this category yet.</div>
      ) : (
        <ul className="space-y-1 mb-2" data-testid={`files-list-${cat}`}>
          {attachments.map((a) => (
            <li key={a.file_id} className="flex items-center gap-2 text-xs" data-testid={`file-${a.file_id}`}>
              <a href={`${api.defaults.baseURL}/project/${p.id}/files/${a.file_id}`} target="_blank" rel="noopener noreferrer"
                className="flex-1 truncate text-orange-600 hover:text-orange-700">
                {a.name || `File ${a.file_id.slice(0, 8)}`}
              </a>
              <span className="text-[10px] text-slate-400 shrink-0">{a.uploaded_by_name}</span>
              {canDelete(a) && (
                <button type="button" onClick={() => del(a.file_id)} data-testid={`file-delete-${a.file_id}`}
                  className="text-slate-400 hover:text-red-600" aria-label="Delete">×</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canUpload ? (
        <label className="flex items-center justify-center gap-1 border-2 border-dashed border-slate-300 rounded-sm p-2 cursor-pointer hover:border-orange-500 text-xs" data-testid={`files-upload-${cat}`}>
          <input type="file" hidden multiple onChange={(e) => uploadFile(e.target.files)} />
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {uploading ? "Uploading…" : "Upload file"}
        </label>
      ) : (
        <p className="text-[10px] text-slate-500 italic">Your role cannot upload to this category.</p>
      )}
    </div>
  );
}

/** Purchase Requests — Production raises; Admin / Super Admin see the list. */
function PurchaseRequestsCard({ p, user }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ item_name: "", quantity: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const role = user?.role;
  const canView = ["super_admin", "admin", "production", "production_coordination"].includes(role);
  const canRaise = ["super_admin", "production", "production_coordination"].includes(role);
  const load = async () => {
    try { const { data } = await api.get(`/project/${p.id}/purchase-requests`); setItems(data); }
    catch { /* noop */ }
  };
  useEffect(() => { if (canView) load(); /* eslint-disable-next-line */ }, []);
  if (!canView) return null;
  const submit = async () => {
    if (!form.item_name.trim() || !form.quantity.trim()) return;
    setSaving(true);
    try {
      await api.post(`/project/${p.id}/purchase-requests`, form);
      toast.success("Request raised — Admin notified");
      setForm({ item_name: "", quantity: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="hk-card p-4" data-testid="purchase-requests-card">
      <div className="hk-tiny-label mb-2">Purchase requests</div>
      {canRaise && (
        <div className="border border-slate-200 rounded-sm p-2 mb-3">
          <input className={FIELD + " mb-1"} placeholder="Item name" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} data-testid="pr-item" />
          <input className={FIELD + " mb-1"} placeholder="Quantity (e.g. 200 pcs)" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="pr-qty" />
          <textarea className={FIELD + " mb-1"} rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pr-notes" />
          <button type="button" onClick={submit} disabled={saving || !form.item_name || !form.quantity} data-testid="pr-submit"
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs">
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Raise request
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="pr-empty">No requests yet.</div>
      ) : (
        <ul className="space-y-1.5" data-testid="pr-list">
          {items.map((it) => (
            <li key={it.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2">
              <div className="font-bold text-slate-900">{it.item_name} · {it.quantity}</div>
              {it.notes && <div className="text-slate-600 mt-0.5">{it.notes}</div>}
              <div className="text-[10px] text-slate-400 mt-1">By {it.raised_by_name} · {new Date(it.raised_at).toLocaleDateString()}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

